import {
	useCreateBillingPortal,
	useCreateCreditsCheckout,
	useGetCredits,
} from "@package/api-client/hooks/billingController";
import { createFileRoute } from "@tanstack/react-router";
import { CreditCard, ExternalLink, Mail, MessageCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/billing/")({
	component: BillingPage,
});

// Money fields from the API are in micros (millionths of a USD).
function formatMicros(micros: number): string {
	return (micros / 1_000_000).toLocaleString(undefined, {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
		maximumFractionDigits: 4,
	});
}

function BillingPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();

	const { data: credits, isLoading } = useGetCredits(orgId);
	const checkout = useCreateCreditsCheckout();
	const portal = useCreateBillingPortal();

	const [amountDollars, setAmountDollars] = useState("");

	const handleBuy = (e: React.FormEvent) => {
		e.preventDefault();
		const dollars = Number.parseFloat(amountDollars);
		if (!Number.isFinite(dollars) || dollars <= 0) return;
		const amountCents = Math.round(dollars * 100);
		checkout.mutate(
			{ orgId, data: { amountCents } },
			{ onSuccess: (data: { url: string }) => window.location.assign(data.url) },
		);
	};

	const handlePortal = () => {
		portal.mutate(
			{ orgId },
			{ onSuccess: (data: { url: string }) => window.location.assign(data.url) },
		);
	};

	return (
		<div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
			<header className="mb-6 flex items-center justify-between">
				<div>
					<p className="font-mono text-xs uppercase tracking-wide text-base-content/55">
						{t("billing.eyebrow")}
					</p>
					<h1
						data-testid="billing-title"
						className="mt-1 text-2xl font-semibold tracking-tight text-base-content"
					>
						{t("billing.title")}
					</h1>
				</div>
				<button
					type="button"
					data-testid="billing-portal-btn"
					className="btn btn-ghost btn-sm"
					disabled={portal.isPending}
					onClick={handlePortal}
				>
					<ExternalLink className="w-4 h-4" />
					{t("billing.managePortal")}
				</button>
			</header>

			{isLoading && (
				<div className="flex justify-center py-12">
					<span className="loading loading-spinner loading-md" />
				</div>
			)}

			{!isLoading && credits && (
				<div className="space-y-6">
					{/* Balance */}
					<section className="rounded-box border border-base-300 bg-base-100 p-5">
						<h2 className="text-sm font-semibold text-base-content">{t("billing.balance")}</h2>
						<p
							data-testid="billing-balance"
							className="mt-2 text-3xl font-semibold tabular-nums text-base-content"
						>
							{formatMicros(credits.balanceMicros)}
						</p>
						<dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 text-sm">
							<div>
								<dt className="text-base-content/55">{t("billing.purchased")}</dt>
								<dd className="tabular-nums">{formatMicros(credits.purchasedMicros)}</dd>
							</div>
							<div>
								<dt className="text-base-content/55">{t("billing.consumed")}</dt>
								<dd className="tabular-nums">{formatMicros(credits.consumedMicros)}</dd>
							</div>
							<div>
								<dt className="text-base-content/55">{t("billing.refunded")}</dt>
								<dd className="tabular-nums">{formatMicros(credits.refundedMicros)}</dd>
							</div>
							<div>
								<dt className="text-base-content/55">{t("billing.feesCharged")}</dt>
								<dd className="tabular-nums">{formatMicros(credits.feesChargedMicros)}</dd>
							</div>
						</dl>
					</section>

					{/* Usage this period */}
					<section className="rounded-box border border-base-300 bg-base-100 p-5">
						<h2 className="text-sm font-semibold text-base-content">
							{t("billing.usageThisPeriod")}
						</h2>
						<div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
							<div className="flex items-center gap-3">
								<Mail className="w-5 h-5 text-base-content/50" />
								<div>
									<p className="text-sm tabular-nums">
										{credits.usageThisPeriod.email} / {credits.planLimits.freeLimitEmail}
									</p>
									<p className="text-xs text-base-content/55">{t("billing.emailUsage")}</p>
								</div>
							</div>
							<div className="flex items-center gap-3 opacity-60">
								<MessageCircle className="w-5 h-5 text-base-content/50" />
								<div>
									<p className="text-sm">
										<span className="badge badge-ghost badge-sm">{t("billing.comingSoon")}</span>
									</p>
									<p className="text-xs text-base-content/55">{t("billing.whatsappUsage")}</p>
								</div>
							</div>
						</div>
					</section>

					{/* Buy credits */}
					<section className="rounded-box border border-base-300 bg-base-100 p-5 max-w-md">
						<h2 className="text-sm font-semibold text-base-content flex items-center gap-2">
							<CreditCard className="w-4 h-4" />
							{t("billing.buyCredits")}
						</h2>
						<form onSubmit={handleBuy} className="mt-3 flex items-end gap-3">
							<div className="form-control flex-1">
								<label className="label" htmlFor="billing-amount">
									<span className="label-text">{t("billing.amountUsd")}</span>
								</label>
								<input
									id="billing-amount"
									data-testid="billing-amount-input"
									type="number"
									min="1"
									step="0.01"
									className="input input-bordered w-full"
									placeholder="50.00"
									value={amountDollars}
									onChange={(e) => setAmountDollars(e.target.value)}
								/>
							</div>
							<button
								type="submit"
								data-testid="billing-buy-btn"
								className="btn btn-primary"
								disabled={checkout.isPending || !amountDollars}
							>
								{checkout.isPending ? t("common.loading") : t("billing.checkout")}
							</button>
						</form>
						{checkout.isError && (
							<p className="mt-2 text-sm text-error">{t("billing.checkoutError")}</p>
						)}
					</section>
				</div>
			)}
		</div>
	);
}
